/**
 * SignalR connection manager for Life4Earth V2.
 *
 * Maintains a single shared connection to the KPI hub.
 * Automatically reconnects with exponential backoff.
 * Components subscribe to events; the manager dispatches
 * the same CustomEvents the app already listens to.
 *
 * Usage:
 *   import { connectKpiHub, disconnectKpiHub } from './signalrConnection';
 *   connectKpiHub();   // call once in App.tsx on mount
 *   disconnectKpiHub(); // call on unmount
 */

import * as signalR from "@microsoft/signalr";
import { getAccessToken } from '../auth/AuthContext';
import { invalidateAndRefetch, invalidateByPrefix, QUERY_KEYS } from './queryCache';

import { API_BASE_URL } from './client';

const API_BASE = API_BASE_URL;

const HUB_URL = `${API_BASE}/hubs/kpi`;

/** Singleton connection instance. */
let connection: signalR.HubConnection | null = null;

/** Whether we intentionally disconnected (don't auto-reconnect). */
let intentionalStop = false;

/** Guard against overlapping connectKpiHub() calls (e.g. React StrictMode double-mount). */
let connecting = false;

/**
 * Hub event names — must match KpiHub.Events on the backend.
 */
const HubEvents = {
  ScheduleUpdated: "ScheduleUpdated",
  FinanceUpdated: "FinanceUpdated",
  MissionUpdated: "MissionUpdated",
  ProjectUpdated: "ProjectUpdated",
  CommunityUpdated: "CommunityUpdated",
  SecurityUpdated: "SecurityUpdated",
  WorkspaceUpdated: "WorkspaceUpdated",
  /**
   * DEV-1023 — emitted by WorkflowRequestsController on every workflow row
   * write (create / update / batch / status change / delete). Lets other
   * users' sessions learn about new role invitations + status changes
   * without manual reloads.
   */
  WorkflowUpdated: "WorkflowUpdated",
} as const;

/**
 * Shape of the KpiUpdateMessage from the server.
 */
interface KpiUpdateMessage {
  projectGuid: string;
  category: string;
  count: number;
}

/**
 * Map hub events → existing CustomEvent names that App.tsx already listens to.
 */
const hubEventToCustomEvent: Record<string, string> = {
  [HubEvents.ScheduleUpdated]: "l4e_schedule_progress_changed",
  [HubEvents.FinanceUpdated]: "l4e_finance_cost_changed",
  [HubEvents.MissionUpdated]: "l4e_mission_updated",
  [HubEvents.ProjectUpdated]: "l4e_project_updated",
  [HubEvents.CommunityUpdated]: "l4e_community_updated",
  [HubEvents.SecurityUpdated]: "l4e_security_updated",
  [HubEvents.WorkspaceUpdated]: "l4e_workspace_updated",
  // DEV-1023. App.tsx already subscribes to this event to refresh the
  // approvals badge (see refreshApprovals in the badge useEffect).
  [HubEvents.WorkflowUpdated]: "l4e_workflows_updated",
};

/**
 * Check if debug logging is enabled for SignalR events.
 */
function isDebugEnabled(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem('l4e:debug') === '1';
  } catch {
    return false;
  }
}

/**
 * Map hub events → query cache keys to invalidate.
 * SecurityUpdated also invalidates projects (permissions affect visibility).
 */
const hubEventToCacheKeys: Record<string, string[]> = {
  [HubEvents.ScheduleUpdated]: [QUERY_KEYS.schedule, QUERY_KEYS.kpiProgress, QUERY_KEYS.curveMeta],
  [HubEvents.FinanceUpdated]: [QUERY_KEYS.finance],
  [HubEvents.MissionUpdated]: [QUERY_KEYS.mission, QUERY_KEYS.kpiConfigs],
  [HubEvents.ProjectUpdated]: [QUERY_KEYS.projects],
  [HubEvents.CommunityUpdated]: [QUERY_KEYS.community],
  [HubEvents.SecurityUpdated]: [QUERY_KEYS.security, QUERY_KEYS.projects],
  [HubEvents.WorkspaceUpdated]: [QUERY_KEYS.workspaceAi],
  [HubEvents.WorkflowUpdated]: [QUERY_KEYS.workflows],
};

/**
 * Debounced cache invalidation — batches rapid SignalR events per cache key.
 * Without this, every individual SignalR push triggers an immediate API refetch,
 * which hammers the backend when events arrive in quick succession.
 */
const INVALIDATION_DEBOUNCE_MS = 500;
const invalidationTimers = new Map<string, ReturnType<typeof setTimeout>>();

function debouncedInvalidate(key: string) {
  const existing = invalidationTimers.get(key);
  if (existing) clearTimeout(existing);
  invalidationTimers.set(
    key,
    setTimeout(() => {
      invalidationTimers.delete(key);
      invalidateAndRefetch(key);
    }, INVALIDATION_DEBOUNCE_MS)
  );
}

/**
 * Register SignalR event handlers on a connection instance.
 *
 * IMPORTANT: The onclose handler must guard against stale connections.
 * During React Strict Mode (dev) the mount/unmount/remount cycle can leave
 * an old connection's onclose firing AFTER a new connection has been set.
 * Without the `conn === connection` guard, the old onclose would null out
 * the new connection reference, orphaning the live connection.
 */
function registerEventHandlers(conn: signalR.HubConnection): void {
  for (const [hubEvent, customEvent] of Object.entries(hubEventToCustomEvent)) {
    conn.on(hubEvent, (msg: KpiUpdateMessage) => {
      if (hubEvent === HubEvents.ProjectUpdated && isDebugEnabled()) {
        console.log(
          `[SignalR] ${hubEvent}: project=${msg.projectGuid} category=${msg.category} count=${msg.count}`
        );
      }

      // Dispatch the same custom event the app already listens to
      // (still needed for version-bump listeners in App.tsx)
      try {
        window.dispatchEvent(
          new CustomEvent(customEvent, {
            detail: {
              project_guid: msg.projectGuid,
              category: msg.category,
              count: msg.count,
              source: "signalr",
            },
          })
        );
      } catch (err) {
        console.warn("[SignalR] Failed to dispatch custom event:", err);
      }

      // Invalidate relevant query cache keys — debounced to batch rapid events.
      // The 500ms window prevents hammering the API when multiple SignalR
      // events arrive in quick succession (e.g. bulk import, reconnect burst).
      const cacheKeys = hubEventToCacheKeys[hubEvent];
      if (cacheKeys) {
        for (const key of cacheKeys) {
          debouncedInvalidate(key);
        }
      }

      // DEV-1039 iter 3 — Cycle 1 fix. SecurityUpdated covers role
      // assignment changes (accept / decline / resign / remove / rescind).
      // The cache keys for project + user role lists are GUID-suffixed
      // (`projectRoles:{guid}`, `userRoles:{guid}`), so the fixed-key
      // invalidation above doesn't reach them. Invalidate by prefix so
      // every per-project / per-user roles cache entry busts.
      if (hubEvent === HubEvents.SecurityUpdated) {
        invalidateByPrefix("projectRoles:");
        invalidateByPrefix("userRoles:");

        // DEV-1088 follow-up. The Mini-Project About-tab TeamSection
        // listens for `l4e_miniproject_team_persisted` to refetch its
        // team roster — the same event the Settings bulk-save store
        // fires. Role acceptance auto-adds a row to
        // mini_project_team_members on the backend, so we need to
        // wake the About tab on the same SignalR push that already
        // fires from MiniProjectRoleInvitationsController.
        // Over-broadcast is fine — the TeamSection handler matches on
        // detail.miniProjectGuid and ignores unrelated GUIDs (full-
        // project SecurityUpdated events won't match any open
        // Mini-Project page's GUID).
        if (msg.projectGuid) {
          try {
            window.dispatchEvent(
              new CustomEvent("l4e_miniproject_team_persisted", {
                detail: {
                  miniProjectGuid: msg.projectGuid,
                  source: "signalr",
                },
              }),
            );
            window.dispatchEvent(new Event("l4e_miniproject_roles_changed"));
          } catch (err) {
            console.warn("[SignalR] Failed to dispatch mini-project refresh events:", err);
          }
        }
      }
    });
  }

  // Connection state logging
  conn.onreconnecting((error) => {
    console.warn("[SignalR] Reconnecting...", error?.message);
  });

  conn.onreconnected((connectionId) => {
    console.log("[SignalR] Reconnected. ConnectionId:", connectionId);
  });

  conn.onclose((error) => {
    // Guard: only act if THIS connection is still the active one.
    // During React Strict Mode double-mount, an old connection's onclose
    // can fire after a new connection has already been established.
    if (conn !== connection) {
      console.log("[SignalR] Stale connection closed (not current), ignoring.", error?.message);
      return;
    }
    console.log("[SignalR] Connection closed.", error?.message);
    connection = null;
    if (!intentionalStop) {
      // Try to reconnect after a delay
      setTimeout(() => {
        console.log("[SignalR] Attempting manual reconnect...");
        connectKpiHub();
      }, 5000);
    }
  });
}

/**
 * Build a SignalR HubConnection with the specified transport options.
 */
function buildConnection(options?: {
  transport?: signalR.HttpTransportType;
  skipNegotiation?: boolean;
}): signalR.HubConnection {
  const conn = new signalR.HubConnectionBuilder()
    .withUrl(HUB_URL, {
      accessTokenFactory: () => getAccessToken() || '',
      ...(options?.transport !== undefined ? { transport: options.transport } : {}),
      ...(options?.skipNegotiation ? { skipNegotiation: true } : {}),
    })
    .withAutomaticReconnect({
      // Exponential backoff: 0s, 2s, 5s, 10s, 30s, then give up
      nextRetryDelayInMilliseconds: (retryContext) => {
        const delays = [0, 2000, 5000, 10000, 30000];
        return retryContext.previousRetryCount < delays.length
          ? delays[retryContext.previousRetryCount]
          : null; // stop retrying
      },
    })
    .configureLogging(signalR.LogLevel.Warning)
    .build();

  // DEV-955: align with server-side SignalR config (Program.cs:30-32).
  // Server uses standard 15s pings + 30s client timeout. Both hubs share
  // this config (AddSignalR is global, not per-hub). See chatConnection.ts
  // for the full reasoning.
  conn.serverTimeoutInMilliseconds = 30_000;
  conn.keepAliveIntervalInMilliseconds = 15_000;

  return conn;
}

/**
 * Start the SignalR connection and register event handlers.
 * Safe to call multiple times — will not create duplicate connections.
 *
 * Transport fallback strategy:
 *   1. Default (negotiate → best transport) — works in Chrome/Safari
 *   2. WebSockets only, skip negotiation — fixes Firefox negotiate failures
 *   3. Server-Sent Events — fallback if WebSocket blocked
 *   4. Long Polling — last resort
 */
export async function connectKpiHub(): Promise<void> {
  console.log("[SignalR] connectKpiHub() called. connection=", !!connection, "connecting=", connecting, "intentionalStop=", intentionalStop);

  // Guard: already connected or another attempt is in flight.
  if (connection) {
    console.log("[SignalR] Already has connection, skipping.");
    return;
  }
  if (connecting) {
    console.log("[SignalR] Connection attempt already in flight, skipping.");
    return;
  }

  intentionalStop = false;
  connecting = true;

  // Transport strategies to try in order.
  const strategies: Array<{
    label: string;
    transport?: signalR.HttpTransportType;
    skipNegotiation?: boolean;
  }> = [
    { label: "default (negotiate)" },
    {
      label: "WebSocket (skip negotiate)",
      transport: signalR.HttpTransportType.WebSockets,
      skipNegotiation: true,
    },
    {
      label: "Server-Sent Events",
      transport: signalR.HttpTransportType.ServerSentEvents,
    },
    {
      label: "Long Polling",
      transport: signalR.HttpTransportType.LongPolling,
    },
  ];

  try {
    for (const strategy of strategies) {
      if (intentionalStop) {
        console.log("[SignalR] Intentional stop — aborting strategy loop.");
        break;
      }

      console.log(`[SignalR] Trying strategy: ${strategy.label}...`);

      let conn: signalR.HubConnection;
      try {
        conn = buildConnection({
          transport: strategy.transport,
          skipNegotiation: strategy.skipNegotiation,
        });
      } catch (buildErr) {
        console.warn(`[SignalR] Failed to build connection for ${strategy.label}:`, buildErr);
        continue;
      }

      registerEventHandlers(conn);
      // DO NOT set `connection = conn` here — wait until start() succeeds.
      // Setting it prematurely allows overlapping calls (from React StrictMode
      // double-mount, onclose retries, or idle-reconnect) to see a half-started
      // connection and silently bail, leaving the app without real-time updates.

      try {
        await conn.start();
        // Only assign to singleton AFTER successful start.
        connection = conn;
        console.log(`[SignalR] Connected via ${strategy.label} to`, HUB_URL);
        return; // success
      } catch (err) {
        console.warn(`[SignalR] ${strategy.label} failed:`, err);
        try { await conn.stop(); } catch { /* ignore cleanup error */ }
      }
    }

    // All strategies failed — retry after delay
    if (!intentionalStop) {
      console.warn("[SignalR] All transport strategies failed. Retrying in 5s...");
      setTimeout(() => connectKpiHub(), 5000);
    }
  } finally {
    connecting = false;
  }
}

/**
 * Gracefully stop the SignalR connection.
 * Call on App unmount to prevent reconnection attempts.
 */
export async function disconnectKpiHub(): Promise<void> {
  intentionalStop = true;
  connecting = false;
  const conn = connection;
  connection = null;
  if (conn) {
    try {
      await conn.stop();
    } catch {
      // Ignore errors during intentional disconnect
    }
  }
}

/**
 * Gracefully disconnect SignalR for idle timeout purposes.
 * Unlike disconnectKpiHub(), this marks the stop as intentional
 * (suppresses auto-reconnect) but allows explicit reconnect later.
 */
export async function idleDisconnect(): Promise<void> {
  intentionalStop = true;
  if (connection) {
    try {
      await connection.stop();
    } catch {
      // Ignore errors during idle disconnect
    }
    connection = null;
  }
}

/**
 * Reconnect SignalR after an idle disconnect.
 * Resets the connection and triggers a full cache refetch for all keys
 * so that stale data from the idle period is replaced.
 */
export async function reconnectAfterIdle(): Promise<void> {
  // Ensure the old connection is cleaned up
  connection = null;
  connecting = false;
  intentionalStop = false;

  // Reconnect
  await connectKpiHub();

  // Invalidate ALL cache keys to force a full refetch
  for (const key of Object.values(QUERY_KEYS)) {
    invalidateAndRefetch(key);
  }
}

/**
 * Get the current connection state (for debug/status display).
 */
export function getConnectionState(): string {
  if (!connection) return "Disconnected";
  switch (connection.state) {
    case signalR.HubConnectionState.Connected:
      return "Connected";
    case signalR.HubConnectionState.Connecting:
      return "Connecting";
    case signalR.HubConnectionState.Reconnecting:
      return "Reconnecting";
    case signalR.HubConnectionState.Disconnecting:
      return "Disconnecting";
    case signalR.HubConnectionState.Disconnected:
      return "Disconnected";
    default:
      return "Unknown";
  }
}
